import { RecordSource, type MutableRecordSource } from 'relay-runtime';

type InteropWriteRestoreFactory = () => (() => void) | null;
type InteropWriteKeyFactory = () => string;
type InteropWriteApply = () => void;

type OptimisticInteropWriteSnapshot = {
	key: string;
	createRestore: InteropWriteRestoreFactory;
	restore: () => void;
	applyWrite: InteropWriteApply;
};

type OptimisticInteropWriteCapture = {
	keys: Set<string>;
	snapshots: OptimisticInteropWriteSnapshot[];
};

export type OptimisticInteropWriteRollback = {
	hasCapturedWrites: () => boolean;
	// Detaches an abandoned optimistic layer from global rollback ordering
	// without restoring cache data. Used for hook unmount cleanup only.
	cleanup: () => void;
	rollback: (options?: { afterRestore?: () => void }) => void;
};

let activeCapture: OptimisticInteropWriteCapture | null = null;
const activeLayers: OptimisticInteropWriteCapture[] = [];

export function __resetOptimisticInteropWriteRollbackForTests(): void {
	// Test-only escape hatch for clearing singleton optimistic layer state after
	// failed assertions. In production this must not allow consumers to mutate
	// the active optimistic rollback stack.
	if (process.env.NODE_ENV !== 'test') {
		return;
	}

	activeCapture = null;
	activeLayers.length = 0;
}

// Runs a Relay compatibility optimistic update while recording supported
// interop writes. Query and fragment writers register their own restore points
// here, giving Relay an Apollo-like optimistic layer for these supported APIs.
export function captureOptimisticInteropWrites(run: () => void): OptimisticInteropWriteRollback {
	const parentCapture = activeCapture;
	const capture: OptimisticInteropWriteCapture = {
		keys: new Set(),
		snapshots: [],
	};

	activeCapture = capture;
	try {
		run();
		if (capture.snapshots.length > 0) {
			activeLayers.push(capture);
		}
	} catch (error) {
		restoreLayer(capture);
		throw error;
	} finally {
		activeCapture = parentCapture;
	}

	return {
		hasCapturedWrites: () => capture.snapshots.length > 0,
		cleanup: () => {
			cleanupLayer(capture);
		},
		rollback: (options) => {
			rollbackLayer(capture, options?.afterRestore);
		},
	};
}

// Called by interop cache writers. Outside an optimistic mutation update this
// intentionally does nothing, so normal writes keep their original behavior and
// do not pay rollback setup cost.
export function recordOptimisticInteropWrite({
	getKey,
	createRestore,
	applyWrite,
}: {
	getKey: InteropWriteKeyFactory;
	createRestore: InteropWriteRestoreFactory;
	applyWrite: InteropWriteApply;
}): void {
	if (!activeCapture) {
		return;
	}

	let key: string;
	try {
		key = getKey();
	} catch {
		return;
	}

	if (activeCapture.keys.has(key)) {
		return;
	}

	const restore = createRestore();
	if (!restore) {
		return;
	}

	activeCapture.keys.add(key);
	activeCapture.snapshots.push({
		key,
		createRestore,
		restore,
		applyWrite,
	});
}

function rollbackLayer(layer: OptimisticInteropWriteCapture, afterRestore?: () => void): void {
	if (layer.snapshots.length === 0) {
		afterRestore?.();
		return;
	}

	const layerIndex = activeLayers.indexOf(layer);
	if (layerIndex === -1) {
		// Once a layer leaves activeLayers it has either already settled or been
		// dropped. Do not replay stale restore points over still-active optimistic
		// layers; just clear the stale tracking state and preserve callback flow.
		cleanupLayer(layer);
		afterRestore?.();
		return;
	}

	const layersToRestore = [...activeLayers];
	const layersToReapply = activeLayers.filter((activeLayer) => activeLayer !== layer);
	activeLayers.length = 0;

	for (let index = layersToRestore.length - 1; index >= 0; index--) {
		restoreLayer(layersToRestore[index]);
	}
	cleanupLayer(layer);

	let afterRestoreError: unknown;
	try {
		afterRestore?.();
	} catch (error) {
		afterRestoreError = error;
	}

	// Apollo keeps remaining optimistic layers active when one mutation settles.
	// Reapply later Relay-compat layers over the restored base/real-response state
	// so concurrent optimistic updates do not disappear.
	for (const layerToReapply of layersToReapply) {
		reapplyLayerSafely(layerToReapply);
	}

	if (afterRestoreError) {
		throw afterRestoreError;
	}
}

function restoreLayer(layer: OptimisticInteropWriteCapture): void {
	// Restore in reverse write order so related query/fragment writes return to
	// the exact state seen before the optimistic update ran.
	for (let index = layer.snapshots.length - 1; index >= 0; index--) {
		try {
			layer.snapshots[index].restore();
		} catch (error) {
			// One bad restore should not prevent the rest of the optimistic layer
			// from rolling back. Keep trying every snapshot and surface the
			// degraded rollback so debugging has the original failure context.
			reportOptimisticInteropRestoreFailure(error);
		}
	}
}

function reapplyLayer(layer: OptimisticInteropWriteCapture): void {
	const snapshotsToReapply = [...layer.snapshots];
	clearLayer(layer);

	for (const snapshot of snapshotsToReapply) {
		const restore = snapshot.createRestore();
		if (!restore) {
			continue;
		}

		layer.keys.add(snapshot.key);
		// Track the restore point before running the write. Some interop writes
		// can partially mutate a cache before throwing; pre-registering lets the
		// safe reapply path restore that partial mutation before dropping the
		// broken optimistic layer.
		layer.snapshots.push({
			...snapshot,
			restore,
		});
		snapshot.applyWrite();
	}

	if (layer.snapshots.length > 0) {
		activeLayers.push(layer);
	}
}

function reapplyLayerSafely(layer: OptimisticInteropWriteCapture): void {
	try {
		reapplyLayer(layer);
	} catch (error) {
		// A broken optimistic layer should not block unrelated later layers from
		// being reapplied, and it should not turn a successful mutation settle path
		// into a rejection. Restore whatever the failed reapply captured, clear
		// its tracking state so future rollback calls no-op, and drop that layer
		// from activeLayers; the warning keeps the degraded state visible.
		restoreLayer(layer);
		cleanupLayer(layer);
		reportOptimisticInteropReapplyFailure(error);
	}
}

function cleanupLayer(layer: OptimisticInteropWriteCapture): void {
	// Cleanup is intentionally weaker than rollback: it only removes tracking
	// state so a stale layer cannot be restored/reapplied by a later mutation.
	const layerIndex = activeLayers.indexOf(layer);
	if (layerIndex !== -1) {
		activeLayers.splice(layerIndex, 1);
	}
	clearLayer(layer);
}

function clearLayer(layer: OptimisticInteropWriteCapture): void {
	layer.keys.clear();
	layer.snapshots = [];
}

export function reportOptimisticInteropFailure(message: string, error: unknown): void {
	// eslint-disable-next-line no-console -- optimistic interop degradation should stay visible without pulling this parity helper into cache bridge monitoring.
	console.warn(`[APOLLO RELAY OPTIMISTIC INTEROP] ${message}`, error);
}

function reportOptimisticInteropReapplyFailure(error: unknown): void {
	reportOptimisticInteropFailure(
		'Failed to reapply an optimistic interop layer. The failed layer was dropped while remaining layers continued to reapply.',
		error,
	);
}

function reportOptimisticInteropRestoreFailure(error: unknown): void {
	reportOptimisticInteropFailure(
		'Failed to restore an optimistic interop write snapshot. Remaining snapshots continued to restore.',
		error,
	);
}

export function createCacheSnapshotRestoreForCache(
	targetCache: 'apollo' | 'relay',
): (() => void) | null {
	return targetCache === 'apollo'
		? createApolloCacheSnapshotRestore()
		: createRelayStoreSnapshotRestore();
}

// Snapshots Relay's record source before an optimistic interop write. Rollback
// publishes saved records through Relay's store so subscribers are notified.
function createRelayStoreSnapshotRestore(): (() => void) | null {
	if (!('GLOBAL_RELAY_ENV' in window)) {
		return null;
	}

	const relayEnv = window['GLOBAL_RELAY_ENV'];
	const relayStore = relayEnv?.getStore?.();
	const source = relayStore?.getSource?.() as MutableRecordSource | undefined;

	if (!relayStore || !source) {
		return null;
	}

	const snapshot = cloneRelayRecordMap(source.toJSON() as RelayRecordMapSnapshot);

	return () => {
		const recordsToRestore = cloneRelayRecordMap(snapshot);

		relayStore.publish(
			RecordSource.create(recordsToRestore as Parameters<typeof RecordSource.create>[0]),
		);
		relayStore.notify();
	};
}

type RelayRecordMapSnapshot = Record<string, Record<string, unknown> | null | undefined>;

// Apollo exposes extract/restore, so snapshot rollback is simpler than Relay's
// record-source publish path.
function createApolloCacheSnapshotRestore(): (() => void) | null {
	if (!('GLOBAL_APOLLO_CLIENT' in window)) {
		return null;
	}

	const apolloCache = window['GLOBAL_APOLLO_CLIENT']?.cache;
	if (!apolloCache?.extract || !apolloCache?.restore) {
		return null;
	}

	const snapshot = apolloCache.extract();

	return () => {
		apolloCache.restore(snapshot);
	};
}

// Relay records are mutable objects. Clone snapshots so later optimistic writes
// cannot mutate the rollback state we captured.
function cloneRelayRecordMap(records: RelayRecordMapSnapshot): RelayRecordMapSnapshot {
	return JSON.parse(JSON.stringify(records)) as RelayRecordMapSnapshot;
}
